
import _ from 'lodash'
import { mapGetters, mapState } from 'vuex'
import { slugify } from '~/util/string'

export default {
    props: {
        drug: {
            type: Object,
            default: null,
        },
        plan: {
            type: String,
            default: null
        },
        defaultSources: {
            type: String,
            default: 'groups'
        }
    },
    data () {
        return {
            costs: null,
            loading: false
        }
    },
    computed: {
        ...mapGetters([
            'canAgent',
            'frequencies',
            'year',
            'groupSlug',
            'groupName',
        ]),
        ...mapState({
            zip_code: state => state.survey?.zip_code,
            fips: state => state.survey?.fips,
            pharmacies: state => state.survey?.pharmacies
        }),
        label () {
            return (this.drug?.label || this.drug?.name || this.drug?.text || '').trim()
        },
        slug () {
            return slugify(this.label)
        },
        package () {
            return this.drug?.package
        },
        dosage () {
            return this.drug?.dosage?.strength && this.drug?.dosage?.strength_units ? `${this.drug.dosage.strength}${this.drug.dosage.strength_units.toLowerCase()}` : null
        },
        form () {
            return this.drug?.dosage?.form ? this.drug.dosage.form.toLowerCase() : null
        },
        quantity () {
            return this.drug?.quantity
        },
        frequency () {
            return this.drug?.frequency
        },
        trumprxUrl () {
            if (!this.costs) {
                return null
            }
            return `${this.costs?.trumprx?.link}`
        },
        goodrxUrl () {
            if (!this.costs) {
                return null
            }
            return `${this.costs?.goodrx?.link}`
        },
        goodrxPrice0 () {
            return _.get(this.costs || {}, 'goodrx.cost')
        },
        goodrxCostsOrdered () {
            let arr = _.get(this.costs || {}, 'goodrx.costs', [])
            arr = _.orderBy(arr, d => d.cost == null ? d.coupons[0] : d.cost, 'asc')
            return arr
        },
        goodrxCostsSlicedOrdered () {
            let arr = _.get(this.costs || {}, 'goodrx.costs', []).slice(0, 5)
            arr = _.orderBy(arr, d => d.cost == null ? d.coupons[0] : d.cost, 'asc')
            return arr
        },
        trumprxPrice0 () {
            return _.get(this.costs || {}, 'trumprx.prices.0')
        },
        costplusdrugsPrice0 () {
            return _.get(this.costs || {}, 'costplusdrugs.prices.0')
        },
        eaglePrice0 () {
            return _.get(this.costs || {}, 'eagle.prices.0')
        },
        costplusdrugs () {
            return _.get(this.costs || {}, 'costplusdrugs.matches')
        },
        trumprx () {
            return _.get(this.costs || {}, 'trumprx.matches')
        },
        eagle () {
            return _.get(this.costs || {}, 'eagle', {})
        },
        groups () {
            return _.get(this.costs || {}, 'groups.results') || []
        },
        groupsGroupedBySource () {
            return this.groups.reduce((acc, d) => {
                if (!acc[d.source]?.results) {
                    acc[d.source] = { results: [] }
                }
                acc[d.source].results.push(d)
                return acc
            }, {})
        },
        groupsGroupedBySourceName () {
            return this.groups.reduce((acc, d) => {
                if (!acc[d.source_name]?.results) {
                    acc[d.source_name] = { results: [] }
                }
                acc[d.source_name].results.push(d)
                return acc
            }, {})
        },
        groupsGroupedBySourceNameListOrdered () {
            return Object.keys(this.groupsGroupedBySourceName).map((key) => {
                const src = this.groupsGroupedBySourceName[key]
                const sourceIf = src?.results[0]?.source_if
                return {
                    source_name: key,
                    source_if: sourceIf,
                    ...src
                }
            }).sort((a, b) => a.results[0].cost - b.results[0].cost)
        },
        visibleGroupsGroupedBySourceNameListOrdered () {
            return this.groupsGroupedBySourceNameListOrdered.filter(group => group.source_if ? this.groupsGroupedBySource[group.source_if]?.results?.length : true)
        },
        groupsPrice0 () {
            return _.get(this.costs || {}, 'groups.results.0.cost')
        },
        groupsPrice0Unit () {
            return _.get(this.costs || {}, 'groups.results.0.cost_unit')
        },
        groupsTieredPrice () {
            return (_.get(this.costs || {}, 'groups.results') || []).filter(d => d.coverage_flags)[0]
        },
        groupsTier () {
            return this.groupsTieredPrice?.coverage_flags?.tier_design
        },
        groupsCovered () {
            return this.groupsTieredPrice?.coverage_flags?.formulary_covered === 'Y'
        },
        groupsCopays () {
            return this.groupsTieredPrice?.copays || {}
        },
        medicaregovUrl () {
            return null
        },
        medicaregovPdpPrice0 () {
            return _.get(this.costs || {}, 'medicaregov.pdps.0.drug_costs.0.estimated_yearly_total') / 12 * this.frequencyPerMonths
        },
        needymedsUrl () {
            return `https://www.needymeds.org/search-programs?initialSearchTab=drugs&drugQuery=${this.label}`
        },
        costplusdrugsUrl () {
            return _.get(this.costs || {}, 'costplusdrugs.link')
        },
        eagleUrl () {
            return _.get(this.costs || {}, 'eagle.link')
        },
        key () {
            return this.ndc || this.label
        },
        ndc () {
            return (this.package?.ndc || '').trim()
        },
        frequencyPerDays () {
            return parseInt(this.frequency?.match(/(\d){2}/))
        },
        frequencyPerMonths () {
            return this.frequencyPerDays / 30
        },
        noResults () {
            return this.costs && !this.costs.goodrx && !this.costs.medicaregov && !this.costs.needymeds && !this.costs.costplusdrugs && !this.costs?.eagle && !this.costs.groups?.results?.length
        },
        canLoadCosts () {
            return true
            // temporary solution till we support multiple price sources
            // return this.groupSlug || this.$route?.params?.slug
        },
    },
    watch: {
        drug: {
            deep: true,
            handler: _.debounce(function () {
                return this.maybeLoadDrugQuotes()
            }, 200)
        },
        plan () {
            this.loadDrugCosts()
        }
    },
    mounted () {
        this.setGlobal('$costs', this)
        return this.loadDrugCosts()
    },
    methods: {
        async loadDrugCosts (options = {}) {
            if (!this.canLoadCosts) {
                return
            }
            this.costs = null
            this.loading = true

            let zip = this.zip_code || this.$store.getters?.client?.zip_code
            if (options.sources && options.sources.includes('goodrx') && !zip) {
                zip = await this.detectZipFromGeolocation()
            }
            try {
                const costs = await this.$api.getDrugCosts({
                    sources: options.sources || this.defaultSources,
                    group_slug: this.groupSlug || this.$route?.params?.slug,
                    zip_code: zip,
                    fips: this.fips,
                    label: this.label,
                    dosage: this.dosage,
                    form: this.form,
                    quantity: this.quantity,
                    frequency: this.frequency,
                    year: this.year,
                    ndc: this.ndc,
                    pharmacies: this.pharmacies?.length ? _.map(this.pharmacies, 'npi').join(',') : null,
                    // todo: @akhoury make this per group setting
                    vips: 'cvs',
                    plan: this.plan
                })
                this.costs = costs
            } catch (e) {
                this.$store.$alert(e)
            } finally {
                this.loading = false
            }
        },
        maybeLoadDrugQuotes () {
            if (this.costs) {
                return this.loadDrugCosts()
            }
        },
        isSourceAvailable (sourceSlug) {
            if (!this.costs?.groups?.results) {
                return false
            }
            return this.costs.groups.results.some(d => d.source === sourceSlug)
        }
    },
}
